import React, { useState } from 'react';
import Icon from '../AppIcon';
import Input from '../ui/Input';


const ICDCodeLookup = ({ onSelect, selectedCodes = [], multiple = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Comprehensive dental diagnosis code database
  const icdCodes = [
    { code: 'K02.9', description: 'Dental caries, unspecified', category: 'Dental' },
    { code: 'K02.51', description: 'Dental caries on pit and fissure surface limited to enamel', category: 'Dental' },
    { code: 'K02.52', description: 'Dental caries on pit and fissure surface penetrating into dentin', category: 'Dental' },
    { code: 'K02.61', description: 'Dental caries on smooth surface limited to enamel', category: 'Dental' },
    { code: 'K02.62', description: 'Dental caries on smooth surface penetrating into dentin', category: 'Dental' },
    { code: 'K02.7', description: 'Dental root caries', category: 'Dental' },
    { code: 'K03.0', description: 'Excessive attrition of teeth', category: 'Dental' },
    { code: 'K03.1', description: 'Abrasion of teeth', category: 'Dental' },
    { code: 'K03.2', description: 'Erosion of teeth', category: 'Dental' },
    { code: 'K04.0', description: 'Pulpitis', category: 'Endodontic' },
    { code: 'K04.1', description: 'Necrosis of pulp', category: 'Endodontic' },
    { code: 'K04.4', description: 'Acute apical periodontitis of pulpal origin', category: 'Endodontic' },
    { code: 'K04.5', description: 'Chronic apical periodontitis', category: 'Endodontic' },
    { code: 'K04.6', description: 'Periapical abscess with sinus', category: 'Endodontic' },
    { code: 'K04.7', description: 'Periapical abscess without sinus', category: 'Endodontic' },
    { code: 'K05.00', description: 'Acute gingivitis, plaque induced', category: 'Periodontal' },
    { code: 'K05.10', description: 'Chronic gingivitis, plaque induced', category: 'Periodontal' },
    { code: 'K05.211', description: 'Aggressive periodontitis, localized, slight', category: 'Periodontal' },
    { code: 'K05.212', description: 'Aggressive periodontitis, localized, moderate', category: 'Periodontal' },
    { code: 'K05.311', description: 'Chronic periodontitis, localized, slight', category: 'Periodontal' },
    { code: 'K05.312', description: 'Chronic periodontitis, localized, moderate', category: 'Periodontal' },
    { code: 'K05.313', description: 'Chronic periodontitis, localized, severe', category: 'Periodontal' },
    { code: 'K05.321', description: 'Chronic periodontitis, generalized, slight', category: 'Periodontal' },
    { code: 'K05.322', description: 'Chronic periodontitis, generalized, moderate', category: 'Periodontal' },
    { code: 'K05.323', description: 'Chronic periodontitis, generalized, severe', category: 'Periodontal' },
    { code: 'K06.0', description: 'Gingival recession', category: 'Periodontal' },
    { code: 'K06.1', description: 'Gingival enlargement', category: 'Periodontal' },
    { code: 'K07.0', description: 'Major anomalies of jaw size', category: 'Orthodontic' },
    { code: 'K07.1', description: 'Anomalies of jaw-cranial base relationship', category: 'Orthodontic' },
    { code: 'K07.2', description: 'Anomalies of dental arch relationship', category: 'Orthodontic' },
    { code: 'K07.3', description: 'Anomalies of tooth position', category: 'Orthodontic' },
    { code: 'K07.4', description: 'Malocclusion, unspecified', category: 'Orthodontic' },
    { code: 'K08.1', description: 'Loss of teeth due to accident, extraction or local periodontal disease', category: 'Dental' },
    { code: 'K08.3', description: 'Retained dental root', category: 'Dental' },
    { code: 'K08.411', description: 'Partial loss of teeth due to caries, class I', category: 'Dental' },
    { code: 'K08.421', description: 'Partial loss of teeth due to periodontal diseases, class I', category: 'Dental' },
    { code: 'K09.0', description: 'Developmental odontogenic cysts', category: 'Oral Surgery' },
    { code: 'K09.1', description: 'Developmental (nonodontogenic) cysts of oral region', category: 'Oral Surgery' },
    { code: 'K12.0', description: 'Recurrent oral aphthae', category: 'Oral Medicine' },
    { code: 'K12.1', description: 'Other forms of stomatitis', category: 'Oral Medicine' },
    { code: 'K13.0', description: 'Diseases of lips', category: 'Oral Medicine' },
    { code: 'K13.21', description: 'Leukoplakia of oral mucosa', category: 'Oral Medicine' },
    { code: 'K13.29', description: 'Other disturbances of oral epithelium', category: 'Oral Medicine' },
    { code: 'Z01.20', description: 'Encounter for dental examination and cleaning without abnormal findings', category: 'Preventive' },
    { code: 'Z01.21', description: 'Encounter for dental examination and cleaning with abnormal findings', category: 'Preventive' }
  ];

  const filteredCodes = searchTerm
    ? icdCodes?.filter(icd => 
        icd?.code?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        icd?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        icd?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      )
    : icdCodes;

  const handleSelect = (icd) => {
    if (multiple) {
      const isSelected = selectedCodes?.some(c => c?.code === icd?.code);
      if (isSelected) {
        onSelect?.(selectedCodes?.filter(c => c?.code !== icd?.code));
      } else {
        onSelect?.([...selectedCodes, icd]);
      }
    } else {
      onSelect?.(icd);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const isSelected = (code) => {
    if (multiple) {
      return selectedCodes?.some(c => c?.code === code);
    }
    return selectedCodes?.code === code;
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          type="text"
          placeholder="Search ICD-10 codes..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e?.target?.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pr-10"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setIsOpen(false);
              }}
              className="text-muted-foreground hover:text-foreground transition-smooth"
            >
              <Icon name="X" size={16} />
            </button>
          )}
          <Icon name="Search" size={16} className="text-muted-foreground" />
        </div>
      </div>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 w-full mt-2 bg-card border border-border rounded-lg shadow-elevation-3 max-h-96 overflow-y-auto">
            {filteredCodes?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Icon name="Search" size={32} className="mx-auto mb-2" />
                <p className="text-sm">No ICD codes found</p>
              </div>
            ) : (
              <div className="py-2">
                {filteredCodes?.map((icd) => (
                  <button
                    key={icd?.code}
                    onClick={() => handleSelect(icd)}
                    className={`w-full text-left px-4 py-3 hover:bg-muted transition-smooth border-l-4 ${
                      isSelected(icd?.code) 
                        ? 'border-primary bg-primary/5' :'border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono font-semibold text-sm text-foreground">
                            {icd?.code}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
                            {icd?.category}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {icd?.description}
                        </p>
                      </div>
                      {isSelected(icd?.code) && (
                        <Icon name="Check" size={16} className="text-primary flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ICDCodeLookup;